import { useApi } from '@src/app/context/Api';
import { Button } from '@src/shared/ui/components/Button';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Loader } from '@src/shared/ui/components/Loader';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { useToast } from '@uhg-abyss/web/hooks/useToast';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Modal } from '@uhg-abyss/web/ui/Modal';
import { SelectInputMulti } from '@uhg-abyss/web/ui/SelectInputMulti';
import { isUndefined } from 'lodash';
import React, { useEffect } from 'react';

/**
 * AttachActionPaths
 *
 * Displays a modal to attach action paths to the risk record.
 *
 * @param eid
 * @param handleSearch
 * @param isOpen
 * @param recordActionPaths
 * @param setIsOpen
 * @returns {Element}
 * @constructor
 */
export function AttachActionPaths({
  eid = '',
  handleSearch = () => {},
  isOpen = false,
  recordActionPaths = [],
  setIsOpen = () => {},
}) {
  const { clearApiCache, useApiQuery } = useApi('ActionPath');
  const { useApiMutation } = useApi('RiskRecord');

  const [ListActionPaths, { data: actionPaths, isFetching, isLoading, refetch: refetchActionPaths }] =
    useApiQuery('ListActionPaths');
  const [RiskRecordActionPathAssociation] = useApiMutation('RiskRecordActionPathAssociation', {
    excludedHttpCodes: [400],
  });

  const defaultValues = {
    actionPaths: [],
  };

  const form = useForm({ defaultValues });
  const { toast } = useToast();

  /**
   * Retrieve the list of action paths.
   */
  useEffect(() => {
    if (isUndefined(actionPaths)) {
      ListActionPaths({
        manualAssociation: 'true',
        statusList: ['ACTIVE'],
      });
    }
  }, [actionPaths]);

  /**
   * handleSubmit
   *
   * Handles the form submission.
   *
   * @param submittedValues
   * @returns {Promise<void>}
   */
  const handleSubmit = async (submittedValues) => {
    const toastId = `actionPath-association-${eid}`;
    toast.show({
      ariaLoadingLabel: 'Attaching Action Paths to EID',
      autoClose: false,
      id: `${toastId}-info`,
      isLoading: true,
      message: 'Attaching Action Paths to EID...',
      title: 'Attaching Action Paths to EID',
      variant: 'info',
    });

    const payload = {
      actionPaths: submittedValues?.actionPaths,
      eids: [eid],
    };

    await RiskRecordActionPathAssociation(payload, {
      onError: () => {
        toast.hide(`${toastId}-info`);
        toast.show({
          id: `${toastId}-error`,
          message: 'Unable to attach Action Paths.',
          title: 'Attaching Action Paths Failed',
          variant: 'error',
        });
        setIsOpen(false);
        form?.setValue('actionPaths', []);
      },
      onSuccess: async () => {
        setIsOpen(false);
        await clearApiCache([
          { apiKey: 'RiskRecord', queryKeys: ['GetRiskRecord'] },
          { apiKey: 'ActionPath', queryKeys: ['ListActionPaths'] },
        ]);
        handleSearch();
        refetchActionPaths();
        toast.hide(`${toastId}-info`);
        toast.show({
          id: `${toastId}-success`,
          message: 'Acton Paths have been successfully attached.',
          title: 'Attached Action Paths to EID',
          variant: 'success',
        });
        form?.setValue('actionPaths', []);
      },
    });
  };

  return (
    <ErrorHandler location="src/features/EidSearch/AttachActionPaths/AttachActionPaths.jsx">
      <Modal
        isOpen={isOpen}
        onClose={() => {
          return setIsOpen(false);
        }}
        title="Attach Action Paths"
      >
        <FormProvider autoComplete="off" onSubmit={handleSubmit} state={form}>
          <Modal.Section>
            <Grid>
              {isLoading || isFetching || isUndefined(actionPaths) ? (
                <Grid.Col
                  span={{
                    xs: '100%',
                  }}
                >
                  <Loader height="100%" width="100%" />
                </Grid.Col>
              ) : (
                <Grid.Col
                  span={{
                    xs: '50%',
                  }}
                >
                  <SelectInputMulti
                    isSearchable
                    label="Action Paths"
                    model="actionPaths"
                    options={actionPaths?.content
                      ?.filter((actionPath) => {
                        return !recordActionPaths?.find((recordActionPath) => {
                          return recordActionPath?.actionPathId === actionPath?.id;
                        });
                      })
                      ?.map((actionPath) => {
                        return {
                          label: actionPath?.name,
                          value: actionPath?.id,
                        };
                      })}
                    placeholder="Select Action Paths --"
                  />
                </Grid.Col>
              )}
            </Grid>
          </Modal.Section>
          <Modal.Section>
            <Divider height={1} />
            <Layout.Group alignLayout="right">
              <Button
                onClick={() => {
                  setIsOpen(false);
                }}
                variant="outline"
              >
                Cancel
              </Button>
              <Button isDisabled={!form?.formState?.isDirty} type="submit" variant="solid">
                Attach
              </Button>
            </Layout.Group>
          </Modal.Section>
        </FormProvider>
      </Modal>
    </ErrorHandler>
  );
}
